import { useEffect, useId, useState, type FormEvent } from 'react';
import { parseYoutubeVideoId, youtubeThumbnailUrl } from '../lib/youtubeUrl.ts';
import { listRecentVideos, type RecentVideo } from '../lib/recentVideos.ts';
import { useI18n } from '../i18n/index.ts';

export type LandingProps = {
  onOpenVideo: (videoId: string) => void;
};

export function Landing({ onOpenVideo }: LandingProps) {
  const { t } = useI18n();
  const inputId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentVideo[]>(() => listRecentVideos());

  useEffect(() => {
    setRecent(listRecentVideos());
  }, []);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const id = parseYoutubeVideoId(value);
    if (!id) {
      setError(t('landingUrlInvalid'));
      return;
    }
    setError(null);
    onOpenVideo(id);
  }

  return (
    <main className="landing">
      <div className="landing__shell">
        <div className="landing__brand">
          <p className="landing__eyebrow">YouTube</p>
          <h1 className="landing__title">{t('appName')}</h1>
          <p className="landing__tagline">{t('landingTagline')}</p>
        </div>

        <form className="landing__form" onSubmit={handleSubmit} noValidate>
          <label className="landing__label" htmlFor={inputId}>
            {t('landingUrlLabel')}
          </label>
          <input
            id={inputId}
            className="landing__input"
            type="url"
            name="youtubeUrl"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="https://www.youtube.com/watch?v=…"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `${inputId}-error` : undefined}
          />
          {error ? (
            <p id={`${inputId}-error`} className="landing__error" role="alert">
              {error}
            </p>
          ) : null}
          <button className="landing__submit" type="submit">
            {t('landingOpen')}
          </button>
        </form>

        {recent.length > 0 ? (
          <section className="landing__recent" data-testid="landing-recent" aria-label={t('recentWatches')}>
            <h2 className="landing__recent-title">{t('recentWatches')}</h2>
            <ul className="landing__recent-list">
              {recent.map((item) => {
                const label = item.title?.trim() || `YouTube · ${item.id.slice(0, 8)}…`;
                return (
                  <li key={`${item.id}-${item.openedAt}`}>
                    <button
                      type="button"
                      className="landing__recent-item"
                      data-testid={`recent-video-${item.id}`}
                      onClick={() => onOpenVideo(item.id)}
                    >
                      <img
                        className="landing__recent-thumb"
                        src={youtubeThumbnailUrl(item.id)}
                        alt=""
                        width={120}
                        height={68}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="landing__recent-meta">
                        <span className="landing__recent-id" title={item.id}>
                          {label}
                        </span>
                        <span className="landing__recent-cta">{t('recentWatchOpen')}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>
    </main>
  );
}
